import axios from "axios";
import { API_BASEURL } from "../utils/baseURL";
import { authHeader } from "../utils/headers";

export interface VerticalListItem {
  id: string;
  name: string;
  assignedContentCount: number;
  affectedUserCount?: number;
}

export interface VerticalListResponse {
  verticals: VerticalListItem[];
}

//  backend serializes errors as top-level { error, message, assignedContentCount? }
export interface VerticalErrorResponse {
  error: string;
  message?: string;
  assignedContentCount?: number;
}

export type VerticalServiceResult<T> = {
  status: number;
  data: T | VerticalErrorResponse;
  response?: { status: number; data: unknown };
};

const BASE = `${API_BASEURL}v1/admin/verticals`;

const normalizeAxiosError = (
  error: unknown,
): VerticalServiceResult<never> => {
  const e = error as { response?: { status: number; data: unknown } };
  const response = e.response ?? (error as { status: number; data: unknown });
  return {
    status: response.status,
    data: response.data as VerticalErrorResponse,
    response,
  };
};

export const listVerticals = async (): Promise<VerticalServiceResult<VerticalListResponse>> =>
  await axios.get(BASE, { headers: authHeader() }).catch(normalizeAxiosError) as VerticalServiceResult<VerticalListResponse>;

export const createVertical = async (
  name: string,
): Promise<VerticalServiceResult<VerticalListItem>> =>
  await axios
    .post(BASE, { name: name.trim() }, { headers: authHeader() })
    .catch(normalizeAxiosError) as VerticalServiceResult<VerticalListItem>;

export const renameVertical = async (
  id: string,
  name: string,
): Promise<VerticalServiceResult<VerticalListItem>> =>
  await axios
    .put(
      `${BASE}/${id}/rename`,
      { name: name.trim() },
      { headers: authHeader() },
    )
    .catch(normalizeAxiosError) as VerticalServiceResult<VerticalListItem>;

export const deleteVertical = async (
  id: string,
): Promise<VerticalServiceResult<Record<string, never>>> =>
  await axios
    .delete(`${BASE}/${id}`, { headers: authHeader() })
    .catch(normalizeAxiosError);
